import { get } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotelinfo } from 'frontend-utils/models';
import type { RateStrategy, Rsbaserate, Rssubtype } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  NO_DATA_SUBSORT,
  RSTYPES_IN_CANCELLATION_POLICY_SECTION,
  TYPE_ID_BEST_FLEX,
  TYPE_ID_MEALTYPE,
  TYPE_ID_MOBILE,
  TYPE_ID_NON_REF,
  TYPE_ID_POS,
  TYPE_ID_SEMI_FLEX,
} from 'lumen/utils/constants/rate-strategy-constants';
import {
  formatFrequencyValue,
  formatStrategyValue,
} from 'lumen/utils/strategy/format-helper-utils';

export default class Rsstrategy extends Model {
  [Type] = 'rsstrategy' as const;

  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;

  @belongsTo('rsstrategy', { async: false, inverse: 'historicEntities' })
  declare latestEntity: Rsstrategy;

  @belongsTo('rate-strategy', { async: false, inverse: 'rsstrategies' })
  declare rateStrategy: RateStrategy;

  @belongsTo('rsbaserate', { async: false, inverse: null })
  declare rsbaserate: Rsbaserate;

  // OpenAPI spec: https://git.mylighthouse.com/OTA-Insight/pricing-insight/-/blob/master/api/openapi-spec/openapi.yaml
  @belongsTo('rssubtype', { async: false, inverse: 'rsstrategies' })
  declare rssubtype: Rssubtype;

  @hasMany('rsstrategy', { async: false, inverse: 'latestEntity' })
  declare historicEntities: HasMany<Rsstrategy>;

  // Discounted rate
  @attr('number') declare discountedPrice: number;
  @attr('number') declare frequencyWeekday: number;
  @attr('number') declare frequencyWeekend: number;
  @attr('boolean') declare isAbsolute: boolean;
  @attr('string') declare message: string;
  // Discount or supplement value (not the rate!)
  @attr('number') declare value: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare rsSelections: Services['rs-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baserateWithFallback() {
    // Not offered edge case
    // In this case we still need to show a fallback price, with the appropriate icons. Do a fallback to the base baserates in this case
    // RS Spec link: https://docs.google.com/document/d/1jhgazIAKAPpRChuw2bNoVIFjF7_fKDoQPkJTCEVPhHk/edit?pli=1#heading=h.enz1mu8jjfho
    if (
      this.isCancellationStrategy &&
      this.isNotOffered &&
      /* eslint-disable @typescript-eslint/no-unsafe-member-access */
      !this.rsSelections.baseBaseRatesPerHotelInfo.get(this.hotelinfo.id)
        .hasNoBaseRate
      /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.rsSelections.baseBaseRatesPerHotelInfo.get(this.hotelinfo.id);
    }

    return this.rsbaserate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get changeOverTime() {
    if (this.hasNoDataForLatestOrHistoricReference) {
      return null;
    }

    // @ts-expect-error: Object is possibly 'undefined'.
    return this.supplementOrDiscount - this.historicEntity.supplementOrDiscount;
  }

  // Used for calculating the subtype position
  // Always align with formattedDiscountedPrice when making changes!
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get discountedPriceWithFallback() {
    // If the discounted price of a cancellation strategy (semi flex or non-refundable) is not offered: fall back on the base baserate (for the subtype position)
    // We don't do this for non-computable since the discounted_price is meaningful for cancellation strategies (see formattedDiscountedPrice)
    // We don't do this for soldout since semi-flex/non-refundable can only be soldout in case of a general soldout (baserate will be soldout too)
    // See https://otainsight.slack.com/archives/G01BUCJAFCN/p1638971948249200
    if (this.isCancellationStrategy && this.isNotOffered) {
      // Extra safety: return 0 if we can't fall back on the base rate for some reason
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return (
        /* eslint-disable @typescript-eslint/no-unsafe-member-access */
        this.rsSelections.baseBaseRatesPerHotelInfo.get(this.hotelinfo.id)
          ?.rate ?? 0
        /* eslint-enable @typescript-eslint/no-unsafe-member-access */
      );
    }

    return this.discountedPrice;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extractDateWeekOffset() {
    return this.rateStrategy?.extractDateWeekOffset;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChangeOverTime() {
    if (this.hasNoDataForLatestOrHistoricReference) {
      return this.intl.t('general.missing');
    }

    if (this.changeOverTime === 0 || this.changeOverTime === null) {
      return ''; // Show empty cell for no change
    }

    return formatStrategyValue(
      this.intl,
      this.hotelAndMarketService,
      this.changeOverTime,
      this.isAbsolute,
      true,
    );
  }

  // Always align with discountedPriceWithFallback when making changes!
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDiscountedPrice() {
    if (this.isSoldOut) {
      return this.intl.t('rates.soldout').toString();
    }

    // Not offered mealtype strategies have a custom string
    if (this.isNotOffered && this.isMealtypeStrategy) {
      return this.intl
        .t(`rates.meal.missing.${this.id!.split('_')[1]}`)
        .toString();
    }

    // Not offered cancellation strategies shouldn't display general.missing as they have a fallback rate (see discountedPriceWithFallback)
    // RS Spec link: https://docs.google.com/document/d/1jhgazIAKAPpRChuw2bNoVIFjF7_fKDoQPkJTCEVPhHk/edit?pli=1#heading=h.enz1mu8jjfho
    if (this.isNotOffered && this.isCancellationStrategy) {
      return this.hotelAndMarketService.formatRate(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.discountedPriceWithFallback,
        { longFormat: false },
      );
    }

    // Non-computable mealtype and cancellation strategies have a meaningful discountedPrice and should not be filtered out!
    // Therefore we check on !discountedPrice instead of this.isNonComputable
    if (this.isMissing || this.isNotOffered || !this.discountedPrice) {
      return this.intl.t('general.missing').toString();
    }

    return this.hotelAndMarketService.formatRate(this.discountedPrice, {
      longFormat: false,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedFrequencyWeekday() {
    if (this.hasNoData) {
      return this.intl.t('general.missing').toString();
    }

    return formatFrequencyValue(this.frequencyWeekday);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedFrequencyWeekend() {
    if (this.hasNoData) {
      return this.intl.t('general.missing').toString();
    }

    return formatFrequencyValue(this.frequencyWeekend);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedHistoricValue() {
    if (!this.historicEntity) {
      return this.intl.t('general.missing').toString();
    }

    return (
      this.historicEntity.noDataMessage ??
      formatStrategyValue(
        this.intl,
        this.hotelAndMarketService,
        this.historicEntity.supplementOrDiscount,
        this.historicEntity.isAbsolute,
      )
    );
  }

  // Attention: formattedValue is the discount/supplement and not the rate!
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    return (
      this.noDataMessage ??
      formatStrategyValue(
        this.intl,
        this.hotelAndMarketService,
        this.supplementOrDiscount,
        this.isAbsolute,
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoData() {
    return (
      this.isMissing ||
      this.isSoldOut ||
      this.isNonComputable ||
      this.isNotOffered
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoDataForLatestOrHistoricReference() {
    return (
      this.hasNoData || !this.historicEntity || this.historicEntity.hasNoData
    );
  }

  get historicEntity(): Rsstrategy | undefined {
    // Use the reference period of the Strategy or Cancellation Policy section depending on the current rstype
    const referencePeriod = RSTYPES_IN_CANCELLATION_POLICY_SECTION.includes(
      this.rstype.name,
    )
      ? this.rsSelections.referencePeriodCancellationPolicySection
      : this.rsSelections.referencePeriodStrategySection;

    return this.historicEntities.find(
      (rsentity) => rsentity.extractDateWeekOffset === referencePeriod,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCancellationStrategy() {
    return (
      // @ts-expect-error: Object is possibly 'null'.
      this.id.startsWith(TYPE_ID_BEST_FLEX) ||
      // @ts-expect-error: Object is possibly 'null'.
      this.id.startsWith(TYPE_ID_SEMI_FLEX) || // @ts-expect-error: Object is possibly 'null'.
      this.id.startsWith(TYPE_ID_NON_REF)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isExcludedFromPosition() {
    if (this.isCancellationStrategy) {
      // Cancellation strategies only need to be excluded in case the strategy is missing
      // In case the strategy is non computable, soldout or not offered we fall back on the base baserate

      // We do a check to see if the base baserate does exist for the current hotel. In case of general soldouts it's possible that this doesn't exist.
      // In that case this shouldn't be taken into account for the position

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { hasNoBaseRate } = this.rsSelections.baseBaseRatesPerHotelInfo.get(
        this.hotelinfo.id,
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.isMissing || hasNoBaseRate;
    }

    return this.hasNoData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMealtypeStrategy() {
    // @ts-expect-error: Object is possibly 'null'.
    return this.id.startsWith(TYPE_ID_MEALTYPE);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMobileStrategy() {
    // @ts-expect-error: Object is possibly 'null'.
    return this.id.startsWith(TYPE_ID_MOBILE);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNonComputable() {
    return this.message === 'rates.noncomputable';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNonRefundableStrategy() {
    // @ts-expect-error: Object is possibly 'null'.
    return this.id.startsWith(TYPE_ID_NON_REF);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNotOffered() {
    return this.message === 'rates.notoffered';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOwnHotel() {
    return this.hotelinfo.id === this.hotelAndMarketService.hotel.hotelinfo.id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPosStrategy() {
    // @ts-expect-error: Object is possibly 'null'.
    return this.id.startsWith(TYPE_ID_POS);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSemiflexStrategy() {
    // @ts-expect-error: Object is possibly 'null'.
    return this.id.startsWith(TYPE_ID_SEMI_FLEX);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSoldOut() {
    return this.message === 'rates.soldout';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get noDataMessage() {
    if (this.isSoldOut) {
      return this.intl.t('rates.soldout').toString();
    }

    if (this.isNotOffered) {
      if (this.isMealtypeStrategy) {
        // we use the subtype id to know which mealtype is missing. Using label would be more clear, but we can't access the subtype directly from here
        return (
          this.intl
            // @ts-expect-error: Object is possibly 'null'.
            .t(`rates.meal.missing.${this.id.split('_')[1]}`)
            .toString()
        );
      }

      if (this.isNonRefundableStrategy) {
        return this.intl.t('strategy.no_non_refundable').toString();
      }

      if (this.isSemiflexStrategy) {
        return this.intl.t('strategy.no_semi_flex').toString();
      }
    }

    if (this.hasNoData) {
      return this.intl.t('general.missing').toString();
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rstype() {
    // TODO: Check if `get()` is still necessary.
    // eslint-disable-next-line ember/no-get
    return get(this.rssubtype, 'rstype');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subsortOrder() {
    // When comparing two strategies with no data, subsort using the following order:
    // 1. Non-computable 2. Not offered 3. Soldout 4. Missing
    if (this.isNonComputable) {
      return NO_DATA_SUBSORT.NON_COMPUTABLE;
    }
    if (this.isNotOffered) {
      return NO_DATA_SUBSORT.NOT_OFFERED;
    }
    if (this.isSoldOut) {
      return NO_DATA_SUBSORT.SOLDOUT;
    }
    if (this.isMissing) {
      return NO_DATA_SUBSORT.MISSING;
    }

    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get supplementOrDiscount() {
    if (this.value === 0) {
      // Zero remains zero (otherwise negative zero is returned)
      return this.value;
    }

    // Mealtypes have supplements (not discounts).
    if (this.isMealtypeStrategy) {
      return this.value;
    }

    // For POS strategies we don't show the discount, we show the difference vs your median rate
    if (this.isPosStrategy) {
      return this.value;
    }

    // Invert the value sign for  discounts: a positive value implies a negative discount
    return this.value * -1;
  }
}
